import { c as _c } from "react/compiler-runtime";
import { FOCUS_RING } from '@common/components/interaction';
import SvgChevronright from '@common/icon/SvgChevronright';
import { cn } from '@core/utils';
import type { YearEndGoodsInstitution } from '@/api/getYearEndGoodsInstitutionList';
import { CustomLink } from '@/app/_components/CustomLink';
import { GoodsLockup } from '@/app/donation/_components/GoodsLockup';
import { HorizontalScroll, Image } from '@/components/client';
import { DONATION_ROUTES } from '@/constants';
import { GOODS_EVENT_LANDING_HREF, GOODS_HOME_IMAGE_URL } from '@/constants/yearEndDonationHome';
import { GOODS_GIFT_LIST_HREF } from '../constants';
import { FixedImage } from '../FixedImage';
import { GoodsSectionHeader } from '../GoodsSectionHeader';
import { GoodsGiftCard, type GoodsGiftCardProps } from './GoodsGiftCard';
import { type GoodsGiftCardItem, toLoopingCardColumns } from './goodsGiftCards';
import { GoodsGiftRollingCarousel } from './GoodsGiftRollingCarousel';
export const GOODS_SECTION_TITLE = <>
    아래 지역에 기부하면 답례품과
    <br className="hidden mobile-tablet:inline" /> 연말 한정 다이어리를 드려요!
  </>;
export const GOODS_SECTION_BODY_CLASS_NAME = 'flex w-full flex-col gap-24 px-40 content-container mobile-tablet:gap-16 mobile-tablet:px-20';
export const GoodsEventBanner = () => {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("group flex items-center gap-12 overflow-hidden rounded-large bg-[#e6f9d4] px-40 py-16", "mobile-tablet:-mx-20 mobile-tablet:rounded-none mobile-tablet:px-20", FOCUS_RING);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <GoodsLockup className="text-[0.75rem]" />;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <CustomLink prefetch={false} href={GOODS_EVENT_LANDING_HREF} className={t0}><span className="flex min-w-0 flex-1 flex-col gap-16 mobile-tablet:max-w-196 mobile-tablet:gap-8">{t1}<span className="flex flex-col gap-4"><strong className="font-800 text-G-500 typo-h4 mobile-tablet:font-700 mobile-tablet:typo-body3">10만원 기부하면 <br className="hidden mobile-tablet:inline" />카카오프렌즈 다이어리 증정</strong><span className="text-Gray-600 typo-caption1">기부자 한정판 · 소진 시 조기 종료</span></span></span><FixedImage src={GOODS_HOME_IMAGE_URL.DESK_BOOK} label={"\uCE74\uCE74\uC624\uD504\uB80C\uC988 \uB2E4\uC774\uC5B4\uB9AC"} sizes="131px" className="ml-auto h-123 w-131 transition-opacity duration-150 group-hover:opacity-80 group-active:opacity-70 motion-reduce:transition-none" imageClassName="object-contain" /></CustomLink>;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export const GoodsGiftListLink = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = cn("group flex items-center gap-8 rounded-small bg-Gray-50 px-16 py-20", "transition-colors duration-150 hover:bg-Gray-200 active:bg-Gray-300 motion-reduce:transition-none", "mobile-tablet:py-16", FOCUS_RING);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <CustomLink prefetch={false} href={GOODS_GIFT_LIST_HREF} className={t0}><span className="flex min-w-0 flex-1 items-center gap-4 typo-body3 mobile-tablet:typo-body4"><span className="shrink-0 font-700 text-Gray-800">답례품 + 굿즈</span><span className="truncate font-500 text-Gray-700">증정 지역 전체보기</span></span><SvgChevronright className="size-16 shrink-0 text-Gray-700 transition-transform duration-150 group-hover:translate-x-1 group-focus-visible:translate-x-1 motion-reduce:transition-none" /></CustomLink>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

//* embla 루프는 컨테이너 gap·padding을 이음매 간격에 넣지 않는다 — 슬라이드 간격은 슬라이드의 margin-right로 준다
export const REGION_ROLLING_PROPS = {
  loop: true,
  autoScroll: {
    speed: 0.5,
    startDelay: 0,
    stopOnInteraction: false,
    stopOnMouseEnter: true
  }
};

//* PC는 1200 영역 끝에서 잘리므로 양 끝 64를 투명하게 흐려 흐르는 느낌을 준다 — 배경색과 무관하게 마스크로 처리
export const REGION_ROLLING_CLASS_NAME = cn('mobile-tablet:-mx-20', 'xl:[mask-image:linear-gradient(to_right,transparent,black_4rem,black_calc(100%_-_4rem),transparent)]');
const GoodsProviderRegions = t0 => {
  const $ = _c(5);
  const {
    institutions
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <h3 className="font-600 text-Gray-800 typo-body3 mobile-tablet:typo-caption1">한정 굿즈 제공 지역</h3>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== institutions) {
    t2 = institutions.map(_temp);
    $[1] = institutions;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = <div className="flex flex-col gap-4">{t1}<HorizontalScroll {...REGION_ROLLING_PROPS} className={REGION_ROLLING_CLASS_NAME} slideClassName="py-4">{t2}</HorizontalScroll></div>;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
};
export const GoodsGiftCardCarousel = t0 => {
  const $ = _c(4);
  const {
    cards
  } = t0;
  let t1;
  if ($[0] !== cards) {
    t1 = toLoopingCardColumns(cards).map(_temp2);
    $[0] = cards;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <GoodsGiftRollingCarousel>{t1}</GoodsGiftRollingCarousel>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};
interface GoodsSectionProps {
  providerInstitutions: YearEndGoodsInstitution[];
  allClosed: boolean;
  cards: GoodsGiftCardItem[];
  className?: string;
}
export const GoodsSection = t0 => {
  const $ = _c(16);
  const {
    providerInstitutions,
    allClosed,
    cards,
    className
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex w-full flex-col gap-24 mobile-tablet:gap-16", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <GoodsSectionHeader eyebrow={"\uC6F0\uB85C \uC5F0\uB9D0\uD55C\uC815 \uAD7F\uC988"} title={GOODS_SECTION_TITLE} moreHref={GOODS_GIFT_LIST_HREF} moreAriaLabel={"\uB2F5\uB840\uD488 + \uAD7F\uC988 \uC99D\uC815 \uC9C0\uC5ED \uC804\uCCB4\uBCF4\uAE30"} moreClassName="mobile-tablet:hidden" />;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <GoodsEventBanner />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== allClosed || $[5] !== providerInstitutions) {
    t4 = !allClosed ? <GoodsProviderRegions institutions={providerInstitutions} /> : <p className="rounded-small bg-Gray-50 px-16 py-12 text-center font-600 text-Gray-700 typo-body4">올해 한정 굿즈가 모두 마감됐어요</p>;
    $[4] = allClosed;
    $[5] = providerInstitutions;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== cards) {
    t5 = cards.length > 0 ? <GoodsGiftCardCarousel cards={cards} /> : null;
    $[7] = cards;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <GoodsGiftListLink />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5) {
    t7 = <div className={GOODS_SECTION_BODY_CLASS_NAME}>{t3}{t4}{t5}{t6}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== t1 || $[14] !== t7) {
    t8 = <section className={t1}>{t2}{t7}</section>;
    $[13] = t1;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  return t8;
};
function _temp(institution) {
  return <CustomLink prefetch={false} key={institution.institutionId} href={DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
    id: institution.institutionRouteId
  })} className={cn("group mr-12 flex w-80 shrink-0 flex-col items-center gap-4 rounded-small", "mobile-tablet:mr-4 mobile-tablet:w-56", FOCUS_RING)}><span className={cn("relative size-80 overflow-hidden rounded-xlarge border border-Gray-black/5 bg-Gray-white", "transition-colors duration-150 group-hover:border-Gray-black/20 group-active:border-Gray-black/30 motion-reduce:transition-none", "mobile-tablet:size-40 mobile-tablet:rounded-small")}><Image fill={true} src={institution.institutionLogoUrl ?? undefined} alt="" sizes="80px" className="object-contain" /></span><span className="w-full truncate text-center font-600 text-Gray-black typo-caption1">{institution.institutionName}</span></CustomLink>;
}
function _temp2(column, columnIndex) {
  return <div key={`${column[0].institution.institutionId}-${columnIndex}`} className="mr-16 flex shrink-0 flex-col gap-16 mobile-tablet:mr-8">{column.map(card => <GoodsGiftCard key={`${card.institution.institutionId}-${columnIndex}`} {...card} />)}</div>;
}